import { memo } from 'react';
import { clsx } from 'clsx';
import Button from '../../../core/components/Button';
import ChevronLeftIcon from '../../../core/components/icons/ChevronLeftIcon';
import ChevronRightIcon from '../../../core/components/icons/ChevronRightIcon';
import Carousel from '../../../core/components/Carousel';
import CurrentStoreLink from '../../../core/components/router/CurrentStoreLink';
import useCarouselActions from '../../../core/hooks/useCarouselActions';
import type { ProductItemMedia } from '../../types';
import type { ProductImageProps } from '../ProductImage/ProductImage';
import ProductImage from '../ProductImage/ProductImage';
import styles from './ProductTileCarousel.module.scss';

const ProductTileCarousel = ({
    productDetailsLink,
    media,
    className,
    paddingForSingle,
    overviewPlacement,
}: {
    productDetailsLink: string;
    media: ProductItemMedia[];
    paddingForSingle?: boolean;
    className?: string;
    overviewPlacement?: ProductImageProps['overviewPlacement'];
}) => {
    const { nextItem, previousItem, handleChangeActions } = useCarouselActions({
        watchControlState: false,
    });

    const mediaWithEmptyImage = media.length
        ? media
        : [{ renditionUrl: undefined }];

    return (
        <div
            data-test-class="ProductTileCarousel"
            className={clsx(styles.productTileCarousel, className, {
                [styles.padding]: paddingForSingle && media.length === 1,
            })}
        >
            {media.length > 1 && (
                <div className={styles.previousItemButton}>
                    <Button iconButton onPress={previousItem}>
                        <ChevronLeftIcon iconLabel="carousel-previous-icon" />
                    </Button>
                </div>
            )}
            <Carousel loop onChangeActions={handleChangeActions}>
                {mediaWithEmptyImage.map(image => (
                    <CurrentStoreLink
                        to={productDetailsLink}
                        key={image.renditionUrl ?? 'empty'}
                        className={styles.item}
                    >
                        <ProductImage
                            renditionSize="medium"
                            url={image.renditionUrl}
                            overviewPlacement={overviewPlacement}
                        />
                    </CurrentStoreLink>
                ))}
            </Carousel>
            {media.length > 1 && (
                <div className={styles.nextItemButton}>
                    <Button iconButton onPress={nextItem}>
                        <ChevronRightIcon iconLabel="carousel-next-icon" />
                    </Button>
                </div>
            )}
        </div>
    );
};

export default memo(ProductTileCarousel);
